import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { Box, Typography } from '@mui/material'
import { Button } from '../../../interaxDesignSystemComponents'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { PersonalityTraitsVisualization } from '../../gamer/pages/components/PersonalityTraitsVisualization'
import type { PersonalityTraitPair } from '../../gamer/pages/helpers/myProfileHelpers'

/**
 * Personality Traits Visualization Dev Test page
 * Displays the PersonalityTraitsVisualization component with mock data
 */
export const PersonalityTraitsVisualizationDevTest: React.FC = () => {
  const navigate = useNavigate()

  const handleBack = () => {
    navigate(-1)
  }

  // Mock personality traits data
  const mockTraits: PersonalityTraitPair[] = [
    {
      id: 'agreeableness',
      left: 'Straightforward',
      right: 'Empathetic',
      value: 75
    },
    {
      id: 'openness',
      left: 'Practical',
      right: 'Experimental',
      value: 65
    },
    {
      id: 'conscientiousness',
      left: 'Adaptable',
      right: 'Structured',
      value: 90
    },
    {
      id: 'extroversion',
      left: 'Focused',
      right: 'Energetic',
      value: 40
    },
    {
      id: 'neuroticism',
      left: 'Stable',
      right: 'Passionate',
      value: 50
    },
    {
      id: 'decisionMaking',
      left: 'Reflective',
      right: 'Decisive',
      value: 80
    },
    {
      id: 'helpingAssisting',
      left: 'Independent',
      right: 'Supportive',
      value: 70
    },
    {
      id: 'communication',
      left: 'Reserved',
      right: 'Expressive',
      value: 60
    }
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        padding: '24px',
        background: INTERAX_COLORS.offBlack,
        '@media (min-width: 1400px)': {
          padding: '60px'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ marginBottom: '32px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Button variant="secondary" onClick={handleBack} icon={<ArrowLeftIcon />} iconPosition="left">
          Back
        </Button>
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.headline2,
            color: INTERAX_COLORS.white
          }}
        >
          Personality Traits Visualization Dev Test
        </Typography>
      </Box>

      {/* Description */}
      <Box sx={{ marginBottom: '32px', maxWidth: '800px' }}>
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body1,
            color: INTERAX_COLORS.thunderGrey,
            marginBottom: '16px'
          }}
        >
          This dev tool demonstrates the PersonalityTraitsVisualization component with mock personality data. The
          component features interactive sliders with spotlight effects that follow mouse movement.
        </Typography>
      </Box>

      {/* Visualization Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          maxWidth: '1200px'
        }}
      >
        {/* Full width example */}
        <Box>
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.headline4,
              color: INTERAX_COLORS.white,
              marginBottom: '16px'
            }}
          >
            Full Width Example
          </Typography>
          <PersonalityTraitsVisualization traits={mockTraits} title="Personality Traits" />
        </Box>

        {/* Narrow width example (simulating side-by-side layout) */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: '24px'
          }}
        >
          <Box>
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.headline4,
                color: INTERAX_COLORS.white,
                marginBottom: '16px'
              }}
            >
              Half Width Example (Left)
            </Typography>
            <PersonalityTraitsVisualization traits={mockTraits.slice(0, 4)} title="Personality Traits" />
          </Box>
          <Box>
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.headline4,
                color: INTERAX_COLORS.white,
                marginBottom: '16px'
              }}
            >
              Half Width Example (Right)
            </Typography>
            <PersonalityTraitsVisualization traits={mockTraits.slice(4)} title="Personality Traits" />
          </Box>
        </Box>

        {/* Mock data display */}
        <Box
          sx={{
            padding: '24px',
            background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            backdropFilter: 'blur(6px)'
          }}
        >
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.headline5,
              color: INTERAX_COLORS.white,
              marginBottom: '16px'
            }}
          >
            Mock Data Used
          </Typography>
          <Box
            component="pre"
            sx={{
              ...INTERAX_TYPOGRAPHY.body3,
              color: INTERAX_COLORS.thunderGrey,
              overflow: 'auto',
              margin: 0,
              fontFamily: 'monospace'
            }}
          >
            {JSON.stringify(mockTraits, null, 2)}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default PersonalityTraitsVisualizationDevTest
